import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { formatCurrency, LOGO_FALLBACK_URL } from './defaultData';
import { getBRAND_FULL, getBRAND_NAME, getLOGO_URL } from './brandConfig';
import {
  NAVY, GOLD, LIGHT_GRAY, TEXT_COLOR, WHITE, PAGE, CONTENT_WIDTH,
  loadImageAsBase64, ensureSpace, addWrappedText, addFooters, savePDF,
  renderNavyHeader,
} from './pdfShared';

// === Local re-exports for convenience (used by proposalPdfExport) ===
// These are now imported from pdfShared by any module that needs them.

// === Utilities: imported from pdfShared.js ===
// loadImageAsBase64, ensureSpace, addWrappedText — all in pdfShared

// === Section Renderers ===

async function renderHeader(doc, quoteData) {
  const ci = quoteData.company_info || {};
  return await renderNavyHeader(doc, {
    logoUrl: ci.logo_url || getLOGO_URL(),
    companyName: ci.name || getBRAND_NAME(),
    acn: ci.acn || '',
    address: ci.address || '',
    phone: ci.phone || '',
    director: ci.director || '',
    LOGO_FALLBACK_URL,
  });
}

function renderQuoteInfo(doc, quoteData, y) {
  doc.setTextColor(...NAVY);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('QUOTATION', PAGE.margin, y);
  y += 8;

  const body = [
    ['Quote No:', quoteData.quote_no || '\u2014', 'Date:', quoteData.date || '\u2014'],
    ['Client:', quoteData.client_name || '\u2014', 'Client ABN:', quoteData.client_abn || '\u2014'],
    ['Project:', quoteData.project_name || '\u2014', 'Revision:', quoteData.revision || '\u2014'],
  ];
  if (quoteData.contact_person) {
    body[1] = ['Client:', quoteData.client_name || '\u2014', 'Contact:', quoteData.contact_person];
    if (quoteData.client_abn) {
      body.splice(2, 0, ['Client ABN:', quoteData.client_abn, '', '']);
    }
  }
  if (quoteData.project_address) {
    body.push(['Project Address:', quoteData.project_address.replace(/\n/g, ', '), '', '']);
  }

  autoTable(doc, {
    startY: y,
    margin: { left: PAGE.margin, right: PAGE.margin },
    theme: 'plain',
    styles: { fontSize: 9, cellPadding: { top: 2.5, bottom: 2.5, left: 3, right: 3 }, textColor: TEXT_COLOR },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 30, textColor: NAVY },
      1: { cellWidth: 55 },
      2: { fontStyle: 'bold', cellWidth: 30, textColor: NAVY },
      3: { cellWidth: 55 },
    },
    body,
  });

  return doc.lastAutoTable.finalY + 10;
}

function buildScopeBody(categories, amountEnabled = true) {
  const body = [];
  const itemStyle = {
    fillColor: LIGHT_GRAY,
    textColor: TEXT_COLOR,
    fontSize: 8.5,
    cellPadding: { left: 8, top: 2, bottom: 2, right: 3 },
  };
  const catStyle = {
    fillColor: NAVY,
    textColor: WHITE,
    fontStyle: 'bold',
    cellPadding: { left: 4, top: 3, bottom: 3, right: 3 },
  };

  for (const cat of categories) {
    if (amountEnabled) {
      body.push([
        { content: cat.name, styles: catStyle },
        { content: formatCurrency(cat.price), styles: { ...catStyle, halign: 'right' } },
      ]);
    } else {
      body.push([
        { content: cat.name, styles: catStyle },
      ]);
    }

    for (const item of cat.items) {
      body.push([
        { content: `    ${item.description}`, styles: itemStyle },
        ...(amountEnabled ? [{ content: '', styles: { fillColor: LIGHT_GRAY } }] : []),
      ]);
    }
  }
  return body;
}

function renderScopeOfWorks(doc, quoteData, y) {
  doc.setTextColor(...NAVY);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('SCOPE OF WORKS', PAGE.margin, y);
  y += 6;

  const amountEnabled = quoteData.amount_enabled !== false;

  let head, colStyles;
  if (amountEnabled) {
    head = [['Description', 'Amount (AUD)']];
    colStyles = { 0: { cellWidth: CONTENT_WIDTH - 35 }, 1: { cellWidth: 35, halign: 'right' } };
  } else {
    head = [['Description']];
    colStyles = { 0: { cellWidth: CONTENT_WIDTH } };
  }

  autoTable(doc, {
    startY: y,
    margin: { left: PAGE.margin, right: PAGE.margin },
    theme: 'grid',
    styles: { fontSize: 9, cellPadding: { top: 2.5, bottom: 2.5, left: 3, right: 3 }, textColor: TEXT_COLOR, lineColor: [200, 200, 200], lineWidth: 0.2 },
    headStyles: { fillColor: NAVY, textColor: WHITE, fontStyle: 'bold', fontSize: 9 },
    head,
    body: buildScopeBody(quoteData.categories || [], amountEnabled),
    columnStyles: colStyles,
  });

  return doc.lastAutoTable.finalY + 10;
}

function renderPriceSummary(doc, subtotal, gstEnabled, gstAmount, total, y) {
  y = ensureSpace(doc, y, 40);

  const body = [
    [{ content: 'Subtotal', styles: { fontStyle: 'bold' } }, formatCurrency(subtotal)],
  ];
  if (gstEnabled) {
    body.push(['GST (10%)', formatCurrency(gstAmount)]);
    body.push([
      { content: 'Total incl. GST', styles: { fontStyle: 'bold', fillColor: NAVY, textColor: WHITE, fontSize: 11 } },
      { content: formatCurrency(total), styles: { fontStyle: 'bold', fillColor: NAVY, textColor: WHITE, fontSize: 11, halign: 'right' } },
    ]);
  } else {
    body.push([
      { content: 'Total (excl. GST)', styles: { fontStyle: 'bold', fillColor: NAVY, textColor: WHITE, fontSize: 11 } },
      { content: formatCurrency(subtotal), styles: { fontStyle: 'bold', fillColor: NAVY, textColor: WHITE, fontSize: 11, halign: 'right' } },
    ]);
  }

  autoTable(doc, {
    startY: y,
    margin: { left: PAGE.width - PAGE.margin - 80, right: PAGE.margin },
    theme: 'plain',
    styles: { fontSize: 9, cellPadding: { top: 3, bottom: 3, left: 4, right: 4 }, textColor: TEXT_COLOR },
    columnStyles: { 0: { cellWidth: 40 }, 1: { cellWidth: 40, halign: 'right', fontStyle: 'bold' } },
    body,
  });

  return doc.lastAutoTable.finalY + 10;
}

function renderPaymentSchedule(doc, quoteData, paymentTotal, retentionPercentage, y) {
  y = ensureSpace(doc, y, 50);

  doc.setTextColor(...NAVY);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('PAYMENT SCHEDULE', PAGE.margin, y);
  y += 6;

  const body = (quoteData.payment_schedule || []).map(node => [
    node.description,
    `${node.percentage}%`,
    formatCurrency((node.percentage / 100) * paymentTotal),
  ]);
  const retAmount = (retentionPercentage / 100) * paymentTotal;
  body.push([
    { content: 'Retention after defects been completed', styles: { fillColor: LIGHT_GRAY, fontStyle: 'bold' } },
    { content: `${retentionPercentage.toFixed(1)}%`, styles: { fillColor: LIGHT_GRAY } },
    { content: retentionPercentage >= 0 ? formatCurrency(retAmount) : '\u2014', styles: { fillColor: LIGHT_GRAY, halign: 'right' } },
  ]);

  autoTable(doc, {
    startY: y,
    margin: { left: PAGE.margin, right: PAGE.margin },
    theme: 'grid',
    styles: { fontSize: 9, cellPadding: { top: 3, bottom: 3, left: 4, right: 4 }, textColor: TEXT_COLOR, lineColor: [200, 200, 200], lineWidth: 0.2 },
    headStyles: { fillColor: NAVY, textColor: WHITE, fontStyle: 'bold' },
    head: [['Description', '%', 'Amount (AUD)']],
    body,
    columnStyles: { 0: { cellWidth: CONTENT_WIDTH - 60 }, 1: { cellWidth: 20, halign: 'center' }, 2: { cellWidth: 40, halign: 'right' } },
  });

  return doc.lastAutoTable.finalY + 10;
}

function renderTextSection(doc, title, text, y, { italic = false } = {}) {
  y = ensureSpace(doc, y, 30);

  doc.setTextColor(...NAVY);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text(title, PAGE.margin, y);
  y += 6;

  doc.setFont('helvetica', italic ? 'italic' : 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...TEXT_COLOR);
  y = addWrappedText(doc, text, PAGE.margin, y, CONTENT_WIDTH, 4.5);
  return y + 6;
}

function renderExclusions(doc, exclusions, y) {
  y = ensureSpace(doc, y, 30);

  doc.setTextColor(...NAVY);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text('EXCLUSIONS & NOTES', PAGE.margin, y);
  y += 6;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...TEXT_COLOR);

  for (const item of exclusions) {
    y = ensureSpace(doc, y, 5);
    const text = typeof item === 'object' ? item.text : item;
    doc.text(`\u2022  ${text}`, PAGE.margin + 3, y);
    y += 5;
  }
  return y + 6;
}

function renderGeneralConditions(doc, text, y) {
  // Quote documents written before this field existed have no general_conditions
  // key at all; an unguarded .split() below turns that into a rejected promise
  // and (from the list pages) a PDF that silently never appears.
  const body = typeof text === 'string' ? text : '';
  y = ensureSpace(doc, y, 30);

  doc.setTextColor(...NAVY);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text('GENERAL CONDITIONS', PAGE.margin, y);
  y += 6;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...TEXT_COLOR);

  for (const line of body.split('\n')) {
    if (line.trim() === '') { y += 3; continue; }

    const indent = Math.min(line.match(/^(\s*)/)[0].length * 1.2, 20);
    const isMajorSection = /^\d+\.\s/.test(line.trim()) && !/^\d+\.\d+\s/.test(line.trim());
    const isSubSection = /^\d+\.\d+\s/.test(line.trim());
    const isTitle = line.trim().startsWith('Standard Terms');

    if (isTitle) { doc.setFont('helvetica', 'bold'); doc.setFontSize(10); }
    else if (isMajorSection) { doc.setFont('helvetica', 'bold'); doc.setFontSize(9); }
    else if (isSubSection) { doc.setFont('helvetica', 'bold'); doc.setFontSize(8); }
    else { doc.setFont('helvetica', 'normal'); doc.setFontSize(8); }

    for (const wl of doc.splitTextToSize(line.trim(), CONTENT_WIDTH - indent)) {
      y = ensureSpace(doc, y, 3.5);
      doc.text(wl, PAGE.margin + indent, y);
      y += 3.5;
    }
  }
  return y;
}

function renderPaymentDetails(doc, paymentDetails, y) {
  const pd = paymentDetails || {};
  const rows = [
    ['Bank', pd.bank_name || '—'],
    ['A/C Name', pd.account_name || '—'],
    ['BSB', pd.bsb || '—'],
    ['A/C #', pd.account_number || '—'],
  ];

  // Estimate height: heading + gold rule + table rows
  const neededHeight = 8 + 4 + rows.length * 8 + 6;
  y = ensureSpace(doc, y, neededHeight);

  // Section heading
  doc.setTextColor(...NAVY);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text('PAYMENT DETAILS', PAGE.margin, y);
  y += 4;

  // Gold divider line
  doc.setDrawColor(...GOLD);
  doc.setLineWidth(0.4);
  doc.line(PAGE.margin, y, PAGE.width - PAGE.margin, y);
  y += 5;

  // Table rows
  doc.setFontSize(9);
  const labelX = PAGE.margin + 2;
  const valueX = PAGE.margin + 38;
  const rowHeight = 7;

  rows.forEach(([label, value]) => {
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...NAVY);
    doc.text(label, labelX, y);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...TEXT_COLOR);
    doc.text(value, valueX, y);
    y += rowHeight;
  });

  return y + 4;
}

function renderAcceptance(doc, quoteData, y) {
  const acc = quoteData.acceptance || {};
  const neededHeight = 12 + 6 + 70; // heading + rule + content
  y = ensureSpace(doc, y, neededHeight);

  // Section heading
  doc.setTextColor(...NAVY);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text('ACCEPTANCE OF QUOTATION', PAGE.margin, y);
  y += 4;

  // Gold divider
  doc.setDrawColor(...GOLD);
  doc.setLineWidth(0.4);
  doc.line(PAGE.margin, y, PAGE.width - PAGE.margin, y);
  y += 5;

  // Intro text
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...TEXT_COLOR);
  doc.text(
    'By signing below, both parties confirm acceptance of this quotation and agree to the terms and conditions outlined above.',
    PAGE.margin, y, { maxWidth: CONTENT_WIDTH }
  );
  y += 10;

  // Two-column layout
  const colWidth = CONTENT_WIDTH / 2 - 6;
  const leftX = PAGE.margin;
  const rightX = PAGE.margin + CONTENT_WIDTH / 2 + 6;
  const labelColor = [107, 114, 128];

  // Column headers
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(...NAVY);
  doc.text('CLIENT', leftX, y);
  doc.text(getBRAND_FULL(), rightX, y);
  y += 7;

  // Name row
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  doc.setTextColor(...labelColor);
  doc.text('NAME', leftX, y);
  doc.text('AUTHORISED BY', rightX, y);
  y += 4;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...NAVY);
  doc.text(acc.client_name || '', leftX, y);
  doc.text(quoteData.company_info?.director || '', rightX, y);
  y += 12;

  // Date row
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  doc.setTextColor(...labelColor);
  doc.text('DATE', leftX, y);
  doc.text('DATE', rightX, y);
  y += 4;

  // Date lines
  const dateFormatted = acc.date ? new Date(acc.date + 'T00:00:00').toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' }) : '';
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...NAVY);
  if (dateFormatted) doc.text(dateFormatted, leftX, y);
  doc.setDrawColor(180, 180, 180);
  doc.setLineWidth(0.3);
  doc.line(leftX, y + 1, leftX + colWidth, y + 1);
  doc.line(rightX, y + 1, rightX + colWidth, y + 1);
  y += 16;

  // Signature row
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  doc.setTextColor(...labelColor);
  doc.text('SIGNATURE', leftX, y);
  doc.text('SIGNATURE', rightX, y);
  y += 14;

  // Signature lines
  doc.setDrawColor(...NAVY);
  doc.setLineWidth(0.5);
  doc.line(leftX, y, leftX + colWidth, y);
  doc.line(rightX, y, rightX + colWidth, y);

  return y + 8;
}

// addFooters and savePDF imported from pdfShared.js

// === Main Export ===

/**
 * Render all quote pages into an existing jsPDF doc.
 * Adds a new page before starting (caller must have set up any preceding pages).
 * Does NOT save the doc — caller is responsible for saving.
 * Returns the doc for chaining.
 */
export async function buildQuoteIntoDoc(doc, quoteData, subtotal, gstAmount, total, paymentTotal, retentionPercentage) {
  doc.addPage();
  let y = await renderHeader(doc, quoteData);
  y = renderQuoteInfo(doc, quoteData, y);
  y = renderScopeOfWorks(doc, quoteData, y);
  y = renderPriceSummary(doc, subtotal, quoteData.gst_enabled, gstAmount, total, y);
  y = renderPaymentSchedule(doc, quoteData, paymentTotal, retentionPercentage, y);
  y = renderTextSection(doc, 'PROJECT COMPLETION', quoteData.project_completion, y);
  y = renderExclusions(doc, quoteData.exclusions, y);
  y = renderTextSection(doc, 'QUOTATION VALIDITY', quoteData.validity_note, y, { italic: true });
  y = renderGeneralConditions(doc, quoteData.general_conditions, y);
  y = renderPaymentDetails(doc, quoteData.payment_details, y);
  renderAcceptance(doc, quoteData, y);
  return doc;
}

/**
 * Compute derived financial values from quoteData.
 * Useful when rendering a quote outside of QuoteEditor.
 */
export function computeQuoteFinancials(quoteData) {
  const categorySum = (quoteData?.categories || []).reduce((sum, cat) => sum + (Number(cat.price) || 0), 0);
  const subtotal = quoteData?.amount_enabled !== false
    ? categorySum
    : (Number(quoteData?.manual_subtotal) || 0);
  const gstAmount = quoteData?.gst_enabled ? subtotal * 0.1 : 0;
  const total = subtotal + gstAmount;
  const paymentTotal = quoteData?.gst_enabled ? total : subtotal;
  const retentionPercentage = 100 - ((quoteData?.payment_schedule || []).reduce(
    (sum, p) => sum + (Number(p.percentage) || 0), 0
  ));
  return { subtotal, gstAmount, total, paymentTotal, retentionPercentage };
}

export async function generateQuotePDF(quoteData, subtotal, gstAmount, total, paymentTotal, retentionPercentage, mode = 'download') {
  const doc = new jsPDF('p', 'mm', 'a4');

  let y = await renderHeader(doc, quoteData);
  y = renderQuoteInfo(doc, quoteData, y);
  y = renderScopeOfWorks(doc, quoteData, y);
  y = renderPriceSummary(doc, subtotal, quoteData.gst_enabled, gstAmount, total, y);
  y = renderPaymentSchedule(doc, quoteData, paymentTotal, retentionPercentage, y);
  y = renderTextSection(doc, 'PROJECT COMPLETION', quoteData.project_completion, y);
  y = renderExclusions(doc, quoteData.exclusions, y);
  y = renderTextSection(doc, 'QUOTATION VALIDITY', quoteData.validity_note, y, { italic: true });
  y = renderGeneralConditions(doc, quoteData.general_conditions, y);
  y = renderPaymentDetails(doc, quoteData.payment_details, y);
  renderAcceptance(doc, quoteData, y);
  addFooters(doc, (quoteData.company_info || {}).name || getBRAND_NAME());

  const clientName = (quoteData.client_name || 'Client').replace(/\s+/g, '_');
  const fileName = `Quote_${quoteData.quote_no || 'DRAFT'}_${clientName}_${quoteData.date || 'undated'}.pdf`;

  if (mode === 'preview') {
    const blob = doc.output('blob');
    window.open(URL.createObjectURL(blob), '_blank');
  } else {
    await savePDF(doc, fileName);
  }
}
